import { action, computed, observable } from 'mobx'
import { observer } from 'mobx-react'
import { applySnapshot, getSnapshot } from 'mobx-state-tree'
import { Component, ReactElement } from 'react'
import { i18n, I18nInterface } from 'utils/localisation'

import { AutocompleteDataAttributes } from '../NetworkAutocomplete/NetworkAutocomplete'
import { AutocompleteResult } from '../NetworkAutocomplete/NetworkAutocompleteOption'
import { NetworkEntitySelector } from '../NetworkAutocomplete/NetworkEntitySelector'
import { IOrganizationResJSON } from '../Organizations/OrganizationsModel'
import { IVATResponseJSON } from '../Vat'
import { ILegalEntityJSON, ILegalEntityResJSON, LegalEntity, TLegalEntity } from './LegalEntitiesModel'
import { LegalEntityDialog } from './LegalEntityDialog'
import { legalEntityToAutocompleteResult } from './LegalEntityOptionUtils'

export interface ILegalEntitySelectorProps extends I18nInterface {
  onSearchEntities: (query: string) => Promise<ILegalEntityResJSON>
  onSelect: (selection: ILegalEntityJSON) => void
  onSearchOrganizations: (query: string) => Promise<IOrganizationResJSON>
  onCreate: (legalEntity: Partial<ILegalEntityJSON>) => Promise<ILegalEntityJSON>
  canModifyDirectory: boolean
  inputId?: string
  value: TLegalEntity | null
  isDisabled?: boolean
  validateVat: (vatId: string) => Promise<IVATResponseJSON>
  filter?: (legalEntity: ILegalEntityJSON) => boolean
  dataAttributes?: AutocompleteDataAttributes
}

export class LeanLegalEntitySelector extends Component<ILegalEntitySelectorProps> {
  @observable
  isDialogOpen = false
  @observable
  newLegalEntity = LegalEntity.create({})

  constructor(props) {
    super(props)
    this.handleSearchEntities = this.handleSearchEntities.bind(this)
    this.toggleDialog = this.toggleDialog.bind(this)
    this.handleDialogCompleted = this.handleDialogCompleted.bind(this)
    this.handleDialogRender = this.handleDialogRender.bind(this)
  }

  handleSearchEntities(query: string): Promise<AutocompleteResult[]> {
    return this.props
      .onSearchEntities(query)
      .then((networkEntities) =>
        networkEntities.data
          .filter((result) => !this.props.filter || this.props.filter(result))
          .map((result) => legalEntityToAutocompleteResult(result))
      )
  }

  toggleDialog(): void {
    this.isDialogOpen = !this.isDialogOpen
  }

  @action
  handleDialogCompleted(legalEntity: TLegalEntity): Promise<ILegalEntityJSON> {
    this.isDialogOpen = false
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, ...newLegalEntity } = getSnapshot(legalEntity)
    applySnapshot(this.newLegalEntity, {})
    return this.props.onCreate(newLegalEntity)
  }

  @computed
  get value(): AutocompleteResult {
    if (this.props.value) {
      return legalEntityToAutocompleteResult(this.props.value.legalEntityJSON as ILegalEntityJSON)
    }
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'Autocomplet... Remove this comment to see the full error message
    return null
  }

  handleDialogRender(handleEntityCreated): ReactElement {
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'ReactElemen... Remove this comment to see the full error message
    if (!this.props.canModifyDirectory) return null

    return (
      <LegalEntityDialog
        open={this.isDialogOpen}
        handleClose={this.toggleDialog}
        legalEntity={this.newLegalEntity}
        searchOrganizations={this.props.onSearchOrganizations}
        onClickSave={(legalEntity) => this.handleDialogCompleted(legalEntity).then(handleEntityCreated)}
        validateVat={this.props.validateVat}
      />
    )
  }

  render() {
    const { inputId, canModifyDirectory, isDisabled, onSelect, dataAttributes } = this.props

    return (
      <NetworkEntitySelector
        type="legalEntity"
        canModifyDirectory={canModifyDirectory}
        inputId={inputId}
        onSearchEntities={this.handleSearchEntities}
        // @ts-expect-error TS(2322) FIXME: Type '(selection: ILegalEntityJSON) => void' is no... Remove this comment to see the full error message
        onSelect={onSelect}
        onFooterSelect={this.toggleDialog}
        renderForm={this.handleDialogRender}
        value={this.value}
        isDisabled={isDisabled}
        dataAttributes={dataAttributes}
      />
    )
  }
}

export const LegalEntitySelector = i18n(observer(LeanLegalEntitySelector))
